// Datadog Browser RUM for the public web views — the pages an emailed link
// opens (layouts/public_card), the marketing site, and the standalone
// password-reset templates.
//
// Its own entrypoint rather than a line in application.js because
// layouts/public_card must not load Turbo: UnsubscribesController answers a
// form POST with a plain 200, which Turbo Drive would break (see its class
// comment). Nothing in this file may import Turbo or Stimulus.
//
// The server decides everything environment- or page-specific and hands it over
// in a <meta name="dd-rum"> tag; see app/views/shared/_datadog_rum.html.erb.
// With no tag — dev, test, and anywhere DD_RUM_APPLICATION_ID is unset — this
// module loads and does nothing.
//
// See .knowledge/infrastructure/datadog_rum_web_analytics.md
//
// The `-slim` build, not `@datadog/browser-rum`: it ships without Session
// Replay, which these pages must never do. Setting sessionReplaySampleRate to 0
// would be a configuration promise; not shipping the recorder at all is a
// guarantee, and saves about a third of the bundle.
import { datadogRum } from "@datadog/browser-rum-slim"
import { redactUrl, redactUrlsInText } from "../rum/redact_url"

function redact(raw) {
  return redactUrl(raw, window.location.origin)
}

function redactText(text) {
  return redactUrlsInText(text, window.location.origin)
}

// An error carries URLs in prose as well as in its own url fields: the SDK
// writes a network failure into `message` as "XHR error GET https://…", and a
// stack frame names wherever it was thrown from. Both need the text pass.
function redactError(error) {
  if (!error) return

  if (error.message) error.message = redactText(error.message)
  if (error.stack) error.stack = redactText(error.stack)
  if (error.resource?.url) error.resource.url = redact(error.resource.url)
  // Chained causes are errors of the same shape, each with its own message.
  if (Array.isArray(error.causes)) error.causes.forEach(redactError)
}

function redactEvent(event) {
  if (event.view) {
    event.view.url = redact(event.view.url)
    // The referrer is another one of these pages as often as it is an email
    // client, so it carries the same tokens.
    if (event.view.referrer) event.view.referrer = redact(event.view.referrer)
  }

  if (event.resource?.url) event.resource.url = redact(event.resource.url)

  redactError(event.error)

  // Long-task and vital events carry none of this; action events can name a
  // target the SDK already masked. Nothing else on an event is a URL.
}

function readConfig() {
  const meta = document.querySelector('meta[name="dd-rum"]')
  if (!meta) return null

  try {
    return JSON.parse(meta.content)
  } catch {
    return null
  }
}

const config = readConfig()

if (config) {
  const { view, user, context, ...credentials } = config

  datadogRum.init({
    ...credentials,

    // The server names the view (see RumInstrumentation): one name per render
    // branch, so `@view.name` is the funnel rather than the URL shape.
    trackViewsManually: true,

    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,

    // Action names come only from data-dd-action-name. Without this the SDK
    // names an action after the element's text, which on these pages includes
    // the recipient's email address and children's names.
    enablePrivacyForActionName: true,

    // Same-origin XHR/fetch gets trace headers so a browser action joins the
    // Rails trace in APM. Nothing on these server-rendered pages fetches yet;
    // the contribution flows will.
    allowedTracingUrls: [window.location.origin],
    traceSampleRate: 100,

    beforeSend: (event) => {
      redactEvent(event)
      return true
    }
  })

  // id + role and nothing else, matching what the iOS app sends: `usr.id` is
  // users.id as a string, which is the key that joins RUM to the database.
  if (user) datadogRum.setUser(user)
  if (context) datadogRum.setGlobalContext(context)

  datadogRum.startView({ name: view.name })
}
